import React, { useEffect, useState } from 'react';
import { notification } from 'antd';
import {
  Body,
  Button,
  Colors,
  Input,
  Icon,
  OptionType,
  Select,
  Textarea,
  Tooltip,
  SegmentedControl,
} from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import {
  Rule,
  Ruleset,
  Milestone,
  RuleSeverity,
  _RulesetConnection,
  GQLOperations,
} from 'types/dt-graphql-apollo-v2';
import { v1 as uuidv1 } from 'uuid';
import { gql, useApolloClient } from '@apollo/client';
import {
  createRuleQuery,
  updateRuleQuery,
} from 'utils/data-validation-graphql';
import { localStorageGet, localStorageSet } from 'utils/localStorage';
import {
  getSchemaTypeOptions,
  getConditionsCount,
  term,
} from 'utils/project-ruleset';
import { DrawerSteps, Step } from 'components/DrawerSteps/DrawerSteps';
import { LabelListEdit } from 'components/LabelList/LabelListEdit';
import { LabelListDisplay } from 'components/LabelList/LabelListDisplay';
import { ConfirmationDialog } from 'components/ConfirmationDialog/ConfirmationDialog';
import { SeverityTag } from 'components/SeverityTag/SeverityTag';
import {
  defaultLogicalRule,
  defaultLogicalRuleFilter,
  emptyLogicalRuleFilter,
} from 'utils/nested-conditions';
import { Schema } from 'hooks/projects';
import {
  LogicalRule,
  logicalRuleToFilter,
  parseFilterToLogicalRule,
} from 'utils/rules';
import { PRIMARY_KEY_FDM } from 'utils/config';
import { DisabledCodeEditor } from 'components/DisabledCodeEditor/DisabledCodeEditor';
import { DrawerWithTitle } from './components/DrawerWithTitle';
import { ConditionsTreeSummary } from './components/Conditions/ConditionsTreeSummary';
import { ConditionItem } from './components/Conditions/ConditionItem';

type UpsertRuleValues = {
  conditionsTree: LogicalRule;
  customerDefinedId: string;
  description: string;
  errorMessage: string;
  filtersTree: LogicalRule;
  labelList: string[];
  name: string;
  ruleset: string;
  schemaType: string;
  severity: RuleSeverity;
};

/** Returns a list of options for ruleset input. */
const getRulesetOptions = (rulesets: Ruleset[] = []) => {
  const rulesetOptions = rulesets.map((ruleset) => ({
    label: ruleset.name,
    value: ruleset.externalId,
  }));
  return [defaultRuleset, ...rulesetOptions];
};

/** Returns a list of options for severity input. */
const getSeverityOptions = () => {
  const severityOptions: { label: string; value: RuleSeverity }[] = [];

  Object.entries(RuleSeverity).forEach((enumEntry) => {
    const option = {
      label: enumEntry[0],
      value: enumEntry[1],
    };
    severityOptions.push(option);
  });
  return severityOptions;
};

/**
 * Initializes form input values to rule properties if one is passed or default values.
 * Rule can be provided in edit mode or when restoring create rule draft.
 * 'ruleset' (id) is taken from the provided ruleset entity, otherwise from the rule itself.
 * 'schemaType' should have value only for independent rules. When in ruleset, it should be ''.
 */
const initializeForm = (
  rule?: Rule,
  selectedRuleset?: Ruleset | null,
  selectedSchema?: Schema
) => ({
  conditionsTree: parseFilterToLogicalRule(
    rule?.rules ?? defaultLogicalRuleFilter,
    selectedSchema
  ) ?? {
    ...defaultLogicalRule,
    id: uuidv1(),
  },
  customerDefinedId: rule?.customerDefinedId ?? '',
  description: rule?.description ?? '',
  errorMessage: rule?.errorMessage ?? '',
  filtersTree: parseFilterToLogicalRule(
    rule?.filters ?? emptyLogicalRuleFilter,
    selectedSchema
  ) ?? {
    ...defaultLogicalRule,
    conditions: [],
    id: uuidv1(),
  },
  labelList: (rule?.tags as string[]) ?? [],
  name: rule?.name ?? '',
  ruleset:
    selectedRuleset?.externalId ?? rule?.rulesets?.items[0]?.externalId ?? '',
  schemaType: rule?.schemaType ?? '',
  severity: (rule?.severity as RuleSeverity) ?? RuleSeverity.Critical,
});

/** Checks if the shape of the draft is the same as expected form values. */
function isValidCreateRuleDraft<T extends object>(draft?: any): draft is T {
  const defaultFormState = initializeForm();

  return Object.entries(defaultFormState).every(
    ([key, value]) => typeof value === typeof draft?.[key]
  );
}

enum ConditionView {
  Basic = 'Basic',
  Advanced = 'Advanced',
}

const defaultRuleset = {
  label: `Select ${term.ruleset}`,
  value: '',
};

const defaultSchemaTypeOption = {
  label: `Select ${term.schemaType}`,
  value: '',
};

const defaultSeverityOption = {
  label: 'Critical',
  value: RuleSeverity.Critical,
};

export type NewRuleDrawerProps = {
  onCancel: VoidFunction;
  visible: boolean;
  resetDrawer: boolean;
  selectedRule?: Rule;
  currentSchemas?: Schema[];
  selectedRuleset: Ruleset | null | undefined;
  selectedMilestone?: Milestone;
  rulesets?: Ruleset[];
  schemaId: string;
};

export const NewRuleDrawer = ({
  onCancel,
  visible,
  resetDrawer,
  selectedRule,
  currentSchemas,
  selectedRuleset,
  selectedMilestone,
  rulesets,
  schemaId,
}: NewRuleDrawerProps) => {
  const [values, setValues] = useState<UpsertRuleValues>(initializeForm());
  const [currentStep, setCurrentStep] = useState(0);
  const [showRulesetFilter, setShowRulesetFilter] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rulesetFiltersTree, setRulesetFiltersTree] =
    useState<LogicalRule | null>(null);
  const [conditionViewToBeSet, setConditionViewToBeSet] = useState<
    ConditionView | string
  >(ConditionView.Basic);
  const [conditionView, setConditionView] = useState<ConditionView | string>(
    ConditionView.Basic
  );
  const [
    confirmConditionViewChangeDialog,
    setConfirmConditionViewChangeDialog,
  ] = useState(false);
  const [conditionCode, setConditionCode] = useState<string>('');
  const [isDraftSaved, setIsDraftSaved] = useState(false);
  const apolloClient = useApolloClient();

  const draftRuleKey = `createRule:collection-${selectedMilestone?.externalId}`; // draft's key in localStorage

  const isCreateMode = visible && selectedRule === undefined;
  const currentRuleset =
    rulesets?.find((ruleset) => ruleset.externalId === values.ruleset) ??
    selectedRuleset;
  const hasRuleset = !!currentRuleset;
  const actualSchemaType =
    (currentRuleset?.schemaType ||
      values.schemaType.trim() ||
      selectedRule?.schemaType) ??
    '';

  const selectedSchema = currentSchemas?.find(
    (schema) => schema.name === actualSchemaType
  );

  const rulesetOptions = getRulesetOptions(rulesets);
  const selectedRulesetOption =
    rulesetOptions.find((ruleset) => ruleset.value === values.ruleset) ??
    defaultRuleset;

  const severityOptions = getSeverityOptions();
  const selectedSeverityOption =
    severityOptions.find((severity) => severity.value === values.severity) ??
    defaultSeverityOption;

  const schemaTypeOptions = getSchemaTypeOptions(currentSchemas);
  const selectedSchemaTypeOption =
    schemaTypeOptions.find(
      (schemaType) => schemaType.value === actualSchemaType
    ) ?? defaultSchemaTypeOption;

  /** Updates partial form state and in create mode saves draft to localStorage. */
  const updateForm = (value: Partial<UpsertRuleValues>) => {
    const newState = { ...values, ...value };

    if (isCreateMode) {
      localStorageSet(draftRuleKey, newState);
      setIsDraftSaved(true);
    }

    setValues(newState);
  };

  /** Reinitializes form state, resets drawer and in create mode removes existing rule draft. */
  const resetForm = () => {
    onResetDrawer();
    setIsDraftSaved(false);
    setValues(initializeForm(selectedRule, selectedRuleset, selectedSchema));

    if (isCreateMode) {
      localStorage.removeItem(draftRuleKey);
    }
  };

  /**
   * When drawer is opened, initialize approriate state for create/edit rule form.
   * If in create mode, attempt to retrieve draft from localStorage.
   */
  useEffect(() => {
    if (!visible) return;

    // When getting the draft, handle the ruleset provided to the component
    const localStorageDraft = localStorageGet<UpsertRuleValues>(draftRuleKey);
    const createRuleDraft =
      isCreateMode &&
      isValidCreateRuleDraft<UpsertRuleValues>(localStorageDraft)
        ? {
            ...localStorageDraft,
            ruleset:
              selectedRuleset?.externalId ?? localStorageDraft?.ruleset ?? '',
          }
        : undefined;

    const initialFormState =
      createRuleDraft ??
      initializeForm(selectedRule, selectedRuleset, selectedSchema);

    setValues(initialFormState);
    setIsDraftSaved(false);
    setConditionCode('');
    setConditionView(ConditionView.Basic);

    if (
      initialFormState.conditionsTree._type === 'LogicalRule' &&
      initialFormState.conditionsTree.children.length === 0 &&
      selectedRule?.rules
    ) {
      setConditionCode(selectedRule.rules);
      setConditionView(ConditionView.Advanced);
    }
  }, [visible]);

  // When ruleset changes, update schemaType and compute new filters tree
  useEffect(() => {
    // Reset values.schemaType when ruleset exists
    if (values.ruleset) updateForm({ schemaType: '' });

    if (currentRuleset?.filters) {
      const formatedRulesetFiltersTree = parseFilterToLogicalRule(
        currentRuleset.filters || '{}',
        selectedSchema
      );
      setRulesetFiltersTree(formatedRulesetFiltersTree);
    }
  }, [values.ruleset]);

  const { valid: totalValidRules, total: totalRules } = getConditionsCount(
    values.conditionsTree
  );
  const { valid: totalValidFilters, total: totalFilters } = getConditionsCount(
    values.filtersTree
  );
  const { valid: totalValidRulesetFilters } =
    hasRuleset && currentRuleset && rulesetFiltersTree
      ? getConditionsCount(rulesetFiltersTree)
      : { valid: 0 };

  const hasInvalidName = !values.name.trim();
  const hasInvalidSchemaType = !actualSchemaType.trim().length;
  const hasInvalidRuleList = totalRules !== totalValidRules;
  const hasInvalidFilterList = totalFilters !== totalValidFilters;

  const hasInvalidValues =
    hasInvalidName ||
    hasInvalidFilterList ||
    hasInvalidRuleList ||
    hasInvalidSchemaType;

  const handleErrorMessages = () => {
    if (hasInvalidName) {
      return 'The provided name is missing or invalid';
    }
    if (hasInvalidSchemaType) {
      return 'The provided schema type is missing or invalid';
    }
    if (hasInvalidFilterList) {
      return 'The provided filters are incomplete or invalid';
    }
    if (hasInvalidRuleList) {
      return 'The provided rule conditions are incomplete or invalid';
    }
    return 'Unable to save the rule due to invalid configurations';
  };

  const onResetDrawer = () => {
    setConditionView(ConditionView.Basic);
    setShowRulesetFilter(false);
    setCurrentStep(0);
  };

  // const validateRelationalChecks = (advanceRules: string | null) => {
  //   if (advanceRules && advanceRules.includes('OR')) {
  //     notification.error({
  //       message: `Could not update ${term.rule}`,
  //       description: `It is not possible to use relational checks with OR operator.`,
  //     });
  //     return false;
  //   }
  //   return true;
  // };

  const onCreateRule = async () => {
    if (creating || hasInvalidValues) return;
    setCreating(true);
    try {
      const rulesetId = values.ruleset;
      const milestoneId = selectedMilestone?.externalId;
      const properties = selectedSchema?.properties || [];
      const parsedConditions = logicalRuleToFilter(
        values.conditionsTree,
        properties
      );
      const parsedFilters = logicalRuleToFilter(values.filtersTree, properties);
      const rule = {
        externalId: uuidv1(),
        projectId: schemaId,
        name: values.name.trim(),
        customerDefinedId: values.customerDefinedId.trim(),
        description: values.description.trim(),
        severity: values.severity,
        errorMessage: values.errorMessage.trim(),
        schemaType: hasRuleset ? null : values.schemaType.trim(),
        tags: values.labelList,
        primaryKeyName: PRIMARY_KEY_FDM,
        space: '',
      };
      const query = createRuleQuery(rule, milestoneId, rulesetId);
      const rules =
        conditionView === ConditionView.Advanced
          ? conditionCode
          : parsedConditions;

      await apolloClient.mutate({
        mutation: gql(query),
        variables: {
          tags: values.labelList,
          rules: JSON.stringify(rules),
          filters: JSON.stringify(parsedFilters),
        },
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      notification.success({
        message: `${term.Rule} created`,
        description: `Successfully created the ${
          term.rule
        } "${values.name.trim()}"`,
      });

      localStorage.removeItem(draftRuleKey);

      onResetDrawer();
      onCancel();
    } catch (err: any) {
      notification.error({
        message: `Could not add ${term.rule}`,
        description: err.toString(),
      });
    } finally {
      setCreating(false);
    }
  };

  const onUpdateRule = async () => {
    if (creating || hasInvalidValues || !selectedRule) return;

    setCreating(true);
    try {
      const rulesetId = values.ruleset;
      const properties = selectedSchema?.properties || [];
      const parsedConditions = JSON.stringify(
        logicalRuleToFilter(values.conditionsTree, properties),
        undefined,
        2
      );
      const parsedFilters = JSON.stringify(
        logicalRuleToFilter(values.filtersTree, properties),
        undefined,
        2
      );

      const updatedRule: Rule = {
        externalId: selectedRule.externalId,
        space: '',
        projectId: schemaId,
        name: values.name.trim(),
        customerDefinedId: values.customerDefinedId.trim(),
        errorMessage: values.errorMessage.trim(),
        description: values.description.trim(),
        severity: values.severity,
        schemaType: hasRuleset ? null : values.schemaType.trim(),
        tags: values.labelList,
        primaryKeyName: PRIMARY_KEY_FDM,
        rulesets: rulesetId
          ? ({
              items: rulesets?.filter(
                (ruleset) => ruleset.externalId === rulesetId
              ),
            } as _RulesetConnection)
          : undefined,
      };
      const query = updateRuleQuery(selectedRule, updatedRule);
      const rules =
        conditionView === ConditionView.Advanced
          ? conditionCode
          : parsedConditions;
      await apolloClient.mutate({
        mutation: gql(query),
        variables: {
          tags: values.labelList,
          rules,
          filters: parsedFilters,
        },
        refetchQueries: [GQLOperations.Query.GetMilestones],
      });
      notification.success({
        message: `${term.Rule} updated`,
        description: `Successfully updated the ${term.rule} "${updatedRule.name}"`,
      });
      onResetDrawer();
      onCancel();
    } catch (err: any) {
      notification.error({
        message: `Could not update ${term.rule}`,
        description: err.toString(),
      });
    } finally {
      setCreating(false);
    }
  };

  const onCancelHandler = () => {
    if (creating) return;
    if (selectedRule && resetDrawer) {
      onResetDrawer();
    }
    setCurrentStep(0);
    onCancel();
  };

  const confirmConditionViewChangeDialogMessage = () => {
    const from =
      conditionViewToBeSet === ConditionView.Basic
        ? 'GraphQL query'
        : 'edit view';
    const to =
      conditionViewToBeSet === ConditionView.Basic
        ? 'edit view'
        : 'GraphQL query';
    return `When switching from the ${from} to the ${to}, the changes that you have made will be discarded. Are you sure you want to switch to ${to}?`;
  };

  const onChangeRuleView = (key: string | number) => {
    const keyStr = String(key);
    if (key === ConditionView.Advanced) {
      try {
        const parsedConditions = logicalRuleToFilter(
          values.conditionsTree,
          selectedSchema?.properties || []
        );
        setConditionCode(JSON.stringify(parsedConditions, undefined, 2));
        setConditionView(keyStr);
      } catch (error) {
        onConfirmConditionViewChangeDialog(keyStr);
      }
    } else {
      // Convert the string to tree
      try {
        const formatedConditionsTree = parseFilterToLogicalRule(
          conditionCode,
          selectedSchema,
          true
        );
        if (!formatedConditionsTree) {
          throw new Error("Can't convert the string to conditions");
        }
        updateForm({ conditionsTree: formatedConditionsTree });
        setConditionView(keyStr);
      } catch (error) {
        onConfirmConditionViewChangeDialog(keyStr);
      }
    }
  };

  const onConfirmConditionViewChangeDialog = (key: string) => {
    setConditionViewToBeSet(key);
    setConfirmConditionViewChangeDialog(true);
  };
  const onCloseConfirmConditionViewChangeDialog = () => {
    setConfirmConditionViewChangeDialog(false);
    setConditionViewToBeSet('');
  };
  const onChangeConditionView = () => {
    setConditionView(conditionViewToBeSet);
    onCloseConfirmConditionViewChangeDialog();
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const renderNameStep = () => (
    <>
      <Body level={2}>
        The general information will be displayed in the {term.rule} overview.
      </Body>
      <StyledInputGroup>
        <Input
          fullWidth
          placeholder="Name"
          value={values.name}
          onChange={(e) => updateForm({ name: e.target.value })}
        />
        <Input
          fullWidth
          placeholder="Rule number (optional)"
          value={values.customerDefinedId}
          onChange={(e) => updateForm({ customerDefinedId: e.target.value })}
        />
      </StyledInputGroup>
      <StyledTextarea
        placeholder="Description"
        value={values.description}
        onChange={(e) => updateForm({ description: e.target.value })}
      />

      <SelectWrapperSeverity severity={values.severity}>
        <StyledInputTitle level={2}>Severity</StyledInputTitle>
        <Select
          theme="ghost"
          closeMenuOnSelect
          value={selectedSeverityOption}
          placeholder="Select severity level"
          onChange={(option: OptionType<RuleSeverity>) =>
            updateForm({ severity: option.value })
          }
          options={getSeverityOptions()}
        />
      </SelectWrapperSeverity>

      <Button type="primary" disabled={hasInvalidName} onClick={nextStep}>
        Next
      </Button>
    </>
  );

  const renderNameDescription = () =>
    values.name || values.description ? (
      <StyledStepDescription>
        {values.name && (
          <Body level={2}>
            <StyledSpanBold>{values.name}</StyledSpanBold>
            {values.customerDefinedId && ` - ${values.customerDefinedId}`}
          </Body>
        )}
        {values.description && <Body level={2}>{values.description}</Body>}
        {values.severity && <SeverityTag severity={values.severity} />}
      </StyledStepDescription>
    ) : null;

  const renderErrorMessageStep = () => (
    <>
      <Body level={2}>
        Enter an error message to be displayed if this {term.rule} fails.
      </Body>
      <Input
        fullWidth
        placeholder="Error message"
        value={values.errorMessage}
        onChange={(e) => updateForm({ errorMessage: e.target.value })}
      />

      <Button type="primary" onClick={nextStep}>
        Next
      </Button>
    </>
  );
  const renderErrorMessageDescription = () =>
    values.errorMessage ? (
      <StyledStepDescription>
        <Body level={2}>{values.errorMessage}</Body>
      </StyledStepDescription>
    ) : null;

  const renderLabelsStep = () => (
    <>
      <Body level={2}>Add labels to group your {term.rule}.</Body>
      <LabelListEdit
        disabled={creating}
        labels={values.labelList}
        onChange={(labelList: string[]) => updateForm({ labelList })}
      />
      <Button type="primary" onClick={nextStep}>
        Next
      </Button>
    </>
  );

  const renderLabelsDescription = () => (
    <StyledStepDescription>
      <LabelListDisplay
        labels={values.labelList.map((label) => ({ text: label, key: label }))}
        limitLabels={7}
      />
    </StyledStepDescription>
  );

  const renderRulesetFilters = () =>
    currentRuleset?.filters && (
      <ConditionsTreeSummary
        prefix={`ruledrawer-rulesetfilter-${currentRuleset.externalId}`}
        conditionsTree={rulesetFiltersTree}
      />
    );

  const renderRulesetStep = () => (
    <>
      <Body level={2}>
        Connect the {term.rule} to a {term.ruleset} with its filter and/or add
        rule-specific filters manually. The objects that match the filter are
        included in the check.
      </Body>
      <Group>
        <StyledInputTitle level={2}>
          Apply filter from {term.ruleset}
        </StyledInputTitle>
        <StyledRulesetRow>
          <SelectWrapperSchemaType>
            <Select
              fullWidth
              closeMenuOnSelect
              value={selectedRulesetOption}
              placeholder={`Select ${term.ruleset}`}
              onChange={(option: OptionType<string>) =>
                updateForm({ ruleset: option.value })
              }
              options={rulesetOptions}
            />
          </SelectWrapperSchemaType>
          {currentRuleset && (
            <>
              <Button
                icon="Link"
                aria-label={`Linked ${term.ruleset}`}
                type="secondary"
                disabled
              />
              <StyledRulesetRowSchemaType level={2}>
                <span>
                  <StyledRulesetRowSchemaTypeName>
                    <StyledSpanBold>{term.SchemaType}:</StyledSpanBold>
                    {actualSchemaType}
                  </StyledRulesetRowSchemaTypeName>
                </span>
              </StyledRulesetRowSchemaType>
              <StyledRulesetRowFilter>
                <Button
                  type="ghost"
                  icon={showRulesetFilter ? 'ChevronUp' : 'ChevronDown'}
                  iconPlacement="right"
                  onClick={() => setShowRulesetFilter(!showRulesetFilter)}
                  disabled={!currentRuleset?.filters}
                >
                  <span>
                    {`Show full ${term.ruleset} ${
                      currentRuleset?.filters?.length === 1
                        ? 'filter'
                        : 'filters'
                    } (${totalValidRulesetFilters})`}
                  </span>
                </Button>
              </StyledRulesetRowFilter>
              <StyledRulesetRowDelete>
                <Button
                  icon="Delete"
                  aria-label={`Remove ${term.ruleset}`}
                  type="secondary"
                  onClick={() => updateForm({ ruleset: '' })}
                />
              </StyledRulesetRowDelete>
            </>
          )}
          {showRulesetFilter && (
            <StyledRulesetFilters>
              {renderRulesetFilters()}
            </StyledRulesetFilters>
          )}
        </StyledRulesetRow>
      </Group>
      {hasRuleset && (
        <Body level={2}>
          <StyledSpanInlineFlex>
            <StyledInfoIcon type="Info" size={21} />
            The {term.ruleset} filter creates a basic filter of objects to
            check. You can manually add rule-specific conditions to refine this
            filter.
          </StyledSpanInlineFlex>
        </Body>
      )}
    </>
  );

  const renderTypesStep = () => (
    <Group>
      <StyledInputTitle level={2}>
        {hasRuleset
          ? `${term.SchemaType} from ${term.ruleset}`
          : `${term.SchemaType}`}
      </StyledInputTitle>
      <SelectWrapperSchemaType>
        <Select
          fullWidth
          theme={hasRuleset ? 'ghost' : 'filled'}
          closeMenuOnSelect
          value={selectedSchemaTypeOption}
          isDisabled={hasRuleset}
          placeholder={`Select ${term.schemaType}`}
          onChange={(option: OptionType<string>) =>
            updateForm({ schemaType: option.value })
          }
          options={schemaTypeOptions}
        />
      </SelectWrapperSchemaType>
    </Group>
  );

  const renderFiltersStep = () => (
    <>
      {!hasRuleset && (
        <StyledText level={2}>
          Define filters to apply checks to a subset of the {term.schemaType}.
        </StyledText>
      )}

      <Group>
        <ConditionItem
          schemas={currentSchemas || []}
          ancestors={[]}
          selectedSchema={selectedSchema}
          root={values.filtersTree}
          condition={values.filtersTree}
          onRootUpdate={(tree) => {
            updateForm({ filtersTree: tree });
          }}
          enabledPropertyComparison={false}
          isFilter
        />
      </Group>
      <StyledButtonNext
        type="primary"
        disabled={hasInvalidSchemaType}
        onClick={nextStep}
      >
        Next
      </StyledButtonNext>
    </>
  );

  const renderFilterDescription = () => {
    const filtersCount = currentRuleset ? totalValidRulesetFilters : 0;
    return (
      <StyledStepDescription>
        {currentRuleset?.name && (
          <Body level={2}>
            <StyledSpanBold>{term.Ruleset}: </StyledSpanBold>
            {currentRuleset.name}
          </Body>
        )}

        <Body level={2}>
          <StyledSpanBold>{term.SchemaType}: </StyledSpanBold>
          {actualSchemaType}
        </Body>

        {currentRuleset && !!filtersCount && (
          <Body level={2}>
            {filtersCount}
            {filtersCount === 1
              ? ` ${term.ruleset} filter`
              : ` ${term.ruleset} filters`}
          </Body>
        )}

        {!!totalValidFilters && (
          <Body level={2}>
            {currentRuleset && !!filtersCount && 'and '}
            {totalValidFilters}
            {totalValidFilters === 1 ? ' filter' : ' filters'}
          </Body>
        )}
      </StyledStepDescription>
    );
  };

  const renderConditionsStep = () => (
    <>
      <Body level={2}>
        Write your {term.rule} conditions via the edit view or GraphQL query.
        The objects that <b>do not</b> match one or more conditions are listed
        in the Data Quality Explorer as having failed the rule.
      </Body>
      <Group>
        {conditionView === ConditionView.Basic && (
          <ConditionItem
            schemas={currentSchemas || []}
            ancestors={[]}
            selectedSchema={selectedSchema}
            root={values.conditionsTree}
            onRootUpdate={(tree) => updateForm({ conditionsTree: tree })}
            condition={values.conditionsTree}
          />
        )}
        {conditionView === ConditionView.Advanced && (
          <StyledEditorContainer>
            <DisabledCodeEditor
              height="250px"
              value={conditionCode}
              onChange={(value) => setConditionCode(value ?? '')}
            />
          </StyledEditorContainer>
        )}
      </Group>
    </>
  );
  const renderConditionsDescription = () => (
    <StyledStepDescription>
      <Body level={2}>
        {totalValidRules} {totalValidRules === 1 ? ' rule' : ' rules'}
      </Body>
    </StyledStepDescription>
  );

  const stepSubtitle = (
    <Button type="ghost" icon="Edit" size="small" aria-label="Edit" />
  );
  const fallBackDescription = 'No details';

  const steps: Step[] = [
    {
      key: 'description',
      title: 'General information',
      subTitle: stepSubtitle,
      description: renderNameDescription() || fallBackDescription,
      content: renderNameStep(),
      invalid: hasInvalidName,
    },
    {
      key: 'errormessage',
      title: 'Error message',
      subTitle: stepSubtitle,
      description: renderErrorMessageDescription() || fallBackDescription,
      content: renderErrorMessageStep(),
      invalid: false,
    },
    {
      key: 'labels',
      title: 'Labels',
      subTitle: stepSubtitle,
      description: values.labelList.length
        ? renderLabelsDescription()
        : fallBackDescription,
      content: renderLabelsStep(),
      invalid: false,
    },
    {
      key: 'filter',
      title: 'Filter',
      subTitle: stepSubtitle,
      description:
        actualSchemaType && !hasInvalidFilterList
          ? renderFilterDescription()
          : fallBackDescription,
      content: (
        <>
          {renderRulesetStep()}
          {renderTypesStep()}
          {renderFiltersStep()}
        </>
      ),
      invalid: hasInvalidSchemaType || hasInvalidFilterList,
    },
    {
      key: 'conditions',
      title: 'Rule conditions',
      subTitle: stepSubtitle,
      subTitleOnOpen: (
        <StyledSegmentedControl
          currentKey={conditionView}
          size="small"
          type="default"
          onButtonClicked={onChangeRuleView}
        >
          <SegmentedControl.Button
            key={ConditionView.Basic}
            icon="DataTable"
            aria-label="Basic view"
            tooltipContent="Basic view"
            tooltipPlacement="top"
            className={conditionView === ConditionView.Basic ? 'active' : ''}
          />
          <SegmentedControl.Button
            key={ConditionView.Advanced}
            icon="Code"
            aria-label="GraphQL editor"
            tooltipContent="GraphQL editor"
            tooltipPlacement="top"
            className={conditionView === ConditionView.Advanced ? 'active' : ''}
          />
        </StyledSegmentedControl>
      ),
      description: !hasInvalidRuleList
        ? renderConditionsDescription()
        : fallBackDescription,
      content: renderConditionsStep(),
      invalid: hasInvalidValues,
    },
  ];

  const renderFooter = () => (
    <StyledFooter>
      <StyledResetFormBox>
        <Button onClick={resetForm} type="tertiary">
          Reset form
        </Button>

        {isDraftSaved && (
          <StyledChangesSavedBox>
            <StyledChangesSavedIcon type="Checkmark" /> Your changes are saved
          </StyledChangesSavedBox>
        )}
      </StyledResetFormBox>

      <Button onClick={onCancelHandler}>Cancel</Button>
      <Tooltip
        content={handleErrorMessages()}
        disabled={!creating && !hasInvalidValues}
      >
        <Button
          type="primary"
          onClick={() => (selectedRule ? onUpdateRule() : onCreateRule())}
          loading={creating}
          disabled={creating || hasInvalidValues}
        >
          {selectedRule ? `Update ${term.rule}` : `Create ${term.rule}`}
        </Button>
      </Tooltip>
    </StyledFooter>
  );

  return (
    <>
      <DrawerWithTitle
        footer={renderFooter()}
        onCancel={onCancelHandler}
        selectedMilestoneName={selectedMilestone?.name}
        subHeader={
          <Body level={2}>A set of one or more conditions to be checked</Body>
        }
        title={selectedRule ? `Edit ${term.rule}` : `Create ${term.rule}`}
        visible={visible}
      >
        <DrawerSteps
          steps={steps}
          currentStep={currentStep}
          onChangeStep={(step: number) => setCurrentStep(step)}
        />
      </DrawerWithTitle>
      {confirmConditionViewChangeDialog && (
        <ConfirmationDialog
          title="Your changes will be discarded"
          message={confirmConditionViewChangeDialogMessage()}
          textOk={`Discard and switch to ${
            conditionViewToBeSet === ConditionView.Basic
              ? 'edit view'
              : 'GraphQL'
          }`}
          onOk={onChangeConditionView}
          width={450}
          onClose={onCloseConfirmConditionViewChangeDialog}
          buttonTypeOk="destructive"
        />
      )}
    </>
  );
};

const StyledSpanBold = styled.span`
  font-weight: 500;
`;

const StyledInfoIcon = styled(Icon)`
  display: inline-flex;
  align-self: baseline;
  width: 21px;
  height: 21px;
  line-height: 14px;
`;

const StyledSpanInlineFlex = styled.span`
  display: inline-flex;
  gap: 0.5rem;
  align-content: baseline;
`;

const StyledStepDescription = styled.div`
  margin-top: 0.5rem;
`;

const StyledText = styled(Body)`
  margin-top: 1.5rem;
`;
const Group = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-direction: column;
`;

const StyledTextarea = styled(Textarea)`
  flex-direction: column;
  textarea {
    min-height: 100px;
  }
`;

const SelectWrapper = styled.div`
  max-width: 500px;
  width: 100%;
  .cogs-select {
    .cogs-select__menu {
      max-width: 250px;
      max-height: 500px;
      overflow: auto;
    }

    .cogs-select__value-container > div {
      width: 100% !important;
    }
    .cogs-select__value-container .cogs-select__input {
      &,
      input {
        width: 100% !important;
        display: inline-block;
      }
    }
    .cogs-select__control {
      max-width: 500px;
      background: ${Colors['decorative--grayscale--white']};
    }
    .cogs-select__control .cogs-select__single-value,
    .cogs-select__menu .cogs-select__option {
      font-style: normal;
      font-weight: 400;
      font-size: var(--cogs-font-size-sm);
      line-height: 20px;
      color: ${Colors['decorative--grayscale--1000']};
      &:hover {
        cursor: pointer;
      }
    }
  }
  &:hover {
    cursor: pointer;
  }
`;

const SelectWrapperSchemaType = styled(SelectWrapper)`
  .cogs-select {
    .cogs-select--container {
      max-width: 180px;
      &.cogs-select--is-disabled .cogs-select__control {
        background: var(--cogs-bg-control--disabled);
      }
    }
  }
`;

const SelectWrapperSeverity = styled(SelectWrapper)<{
  severity?: RuleSeverity;
}>`
  max-width: 150px !important;
  .cogs-select .cogs-select__control {
    ${({ severity }) =>
      severity === RuleSeverity.Critical &&
      `
      .cogs-select__value-container > .cogs-select__single-value, .cogs-select__indicators .cogs-select__dropdown-indicator {
        color: var(--cogs-text-danger) !important;
      }
      background: var(--cogs-bg-status-small--danger) !important;
    `}
    ${({ severity }) =>
      severity === RuleSeverity.Warning &&
      `
      .cogs-select__value-container > .cogs-select__single-value, .cogs-select__indicators .cogs-select__dropdown-indicator {
        color: var(--cogs-text-warning) !important;
      }
      background: var(--cogs-bg-status-small--warning) !important;
    `}
    ${({ severity }) =>
      severity === RuleSeverity.Minor &&
      `
      .cogs-select__value-container > .cogs-select__single-value, .cogs-select__indicators .cogs-select__dropdown-indicator {
        color: var(--cogs-text-status-small--default) !important;
      }
      background: var(--cogs-bg-status-small--default) !important;
    `}
    ${({ severity }) =>
      severity === RuleSeverity.None &&
      `
      .cogs-select__value-container > .cogs-select__single-value, .cogs-select__indicators .cogs-select__dropdown-indicator {
        color: var(--cogs-text-status-small--unknown) !important;
      }
      background: var(--cogs-bg-status-small--unknown) !important;
    `}
  }
`;

const StyledButtonNext = styled(Button)`
  margin-top: 2.5rem;
`;

const StyledFooter = styled.div`
  align-items: center;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem 2rem;
`;

const StyledResetFormBox = styled.div`
  display: flex;
  margin-right: auto;
`;

const StyledChangesSavedBox = styled.div`
  align-items: center;
  display: flex;
  margin-left: 1em;
`;

const StyledChangesSavedIcon = styled(Icon)`
  color: ${Colors['decorative--green--300']};
  margin-right: 0.5em;
`;

const StyledRulesetRow = styled.div`
  display: grid;
  gap: 0 0.5rem;
  grid-template-columns: auto auto 1fr 1fr auto;
  align-items: center;
`;

const StyledRulesetRowSchemaTypeName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  ${StyledSpanBold} {
    margin-right: 0.25rem;
  }
`;

const StyledRulesetRowSchemaType = styled(Body)`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  > Button {
    margin-right: 0;
  }
  > span {
    display: inline-flex;
    overflow: hidden;
  }
`;

const StyledRulesetRowFilter = styled.div`
  display: inline-flex;
  justify-self: flex-end;
  > Button {
    margin-right: 0;
  }
`;

const StyledRulesetRowDelete = styled.div`
  > Button {
    margin-right: 0;
  }
  display: inline-flex;
  justify-self: flex-end;
`;

const StyledRulesetFilters = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 1rem 0;
  gap: 0.35rem;
  grid-column-start: 3;
  grid-column-end: 5;
`;

const StyledSegmentedControl = styled(SegmentedControl)`
  height: 30px;
  span {
    overflow: visible;
  }
  span.elevated {
    height: 26px;
    border-radius: 0;
    box-shadow: none;
    button {
      background: transparent;
    }
  }
  .active {
    height: 24px !important;
    background: ${Colors['decorative--grayscale--white']} !important;
    border-radius: 5px !important;
    box-shadow:
      0 0 1px rgb(0 0 0 / 8%),
      0 1px 2px rgb(0 0 0 / 8%),
      0 1px 4px rgb(0 0 0 / 6%) !important;
  }
`;

const StyledEditorContainer = styled.div`
  border-radius: 6px;
  border: 2px solid var(--cogs-border-default);
  background: var(--cogs-bg-accent);
  > .cogs-body-2 {
    border-radius: 6px 6px 0 0;
    padding: 0.5rem;
    display: inline-flex;
    border-bottom: 1px solid var(--cogs-border-default);
  }
  section {
    border-radius: 0 0 6px 6px;
    overflow: hidden;
    .margin {
      background: var(--cogs-bg-hover);
      border-right: 1px solid var(--cogs-border-default);
      width: 48px !important;
      .margin-view-overlays .line-numbers {
        color: var(--cogs-text-primary);
        left: auto;
        right: 1rem;
      }
    }
  }
`;

const StyledInputTitle = styled(Body)`
  padding-bottom: 6px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
`;

const StyledInputGroup = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 0.5rem;
  max-width: 500px;
  .title {
    font-size: var(--cogs-b2-font-size);
  }
`;
